import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { outpostStyles, FadingVideo, BlurText, OutpostNavbar } from '../components/OutpostShared';

const FAQItem = ({ q, a }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-black/10">
      <button 
        onClick={() => setIsOpen(!isOpen)} 
        className="w-full flex justify-between items-center py-6 text-left focus:outline-none group"
      >
        <span className="font-heading italic text-2xl md:text-3xl text-black pr-8 group-hover:text-black/70 transition-colors">{q}</span>
        <span className="text-black/40 flex-shrink-0 bg-black/5 rounded-full p-2 group-hover:bg-black/10 transition-colors">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={`w-5 h-5 transition-transform duration-300 ${isOpen ? 'rotate-45' : ''}`}>
            <path d="M12 5v14M5 12h14" />
          </svg>
        </span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }} 
            animate={{ height: 'auto', opacity: 1 }} 
            exit={{ height: 0, opacity: 0 }} 
            className="overflow-hidden"
          >
            <p className="pb-8 text-black/70 font-body text-lg leading-relaxed max-w-3xl pt-2">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function SocialMediaMarketing() {
  const items = [
    "24/7 Platform Management", "Analytics & Reporting", "Community Management", 
    "Content Strategy", "Social Media Strategy"
  ];

  const videos = [
    "https://adilo.bigcommand.com/ed196b8d-978f-40f7-9066-f8ac229a0d78.mp4",
    "https://adilo.bigcommand.com/baff7227-88b1-4109-b57d-e071f03ed8dd.mp4",
    "https://adilo.bigcommand.com/f1106034-31d5-46bb-8415-ddfeae673303.mp4",
    "https://adilo.bigcommand.com/4f3a6ebd-523e-45c5-a65b-27dc9b0355ce.mp4"
  ];

  const problemStats = [
    { stat: "5.22B", text: "Social users worldwide, yet average organic reach on Facebook has dropped below 5.2%." },
    { stat: "90%", text: "Of consumers buy from brands they follow—if those brands are active and authentic." },
    { stat: "1 Hour", text: "Without a response, brands see a 50% drop in customer sentiment and purchase intent." },
    { stat: "78%", text: "Of consumers say a brand's social presence directly impacts whether they trust that brand." }
  ];

  const capabilities = [
    { title: "24/7 Platform Management", desc: "Your brand never sleeps. We monitor, post, and engage across every platform around the clock—ensuring your audience always has someone to connect with." },
    { title: "Analytics & Reporting", desc: "No vanity metrics. We deliver transparent, actionable reporting that tracks what matters—reach, conversions, and ROI—so every decision is backed by data." },
    { title: "Community Management", desc: "We don't just reply to comments—we build relationships. Proactive engagement, sentiment monitoring, and audience nurturing that turns followers into advocates." },
    { title: "Content Strategy", desc: "Platform-native content planned and created with purpose. From editorial calendars to trending formats—every post has a job to do." },
    { title: "Social Media Strategy", desc: "A holistic roadmap aligned to your business goals. We define your voice, map the competitive landscape, and build a strategy designed to grow and convert." }
  ];

  const benefits = [
    { icon: "📈", title: "Consistent Growth", desc: "Steady, organic follower growth fueled by strategy, not algorithms or gimmicks." },
    { icon: "💬", title: "Higher Engagement", desc: "More comments, shares, saves, and meaningful conversations within your community." },
    { icon: "🛡️", title: "Brand Protection", desc: "24/7 monitoring catches issues before they become crises, protecting your reputation." },
    { icon: "🎯", title: "Audience Clarity", desc: "Deep understanding of who your followers are, what they want, and when they're active." },
    { icon: "🔄", title: "Content That Converts", desc: "Every post moves your audience closer to action—whether a click, a sign-up, or a purchase." },
    { icon: "📊", title: "Full Transparency", desc: "Monthly reporting with clear metrics, insights, and next steps to scale performance." }
  ];

  const platforms = ["Instagram", "Facebook", "TikTok", "LinkedIn", "X (Twitter)", "Pinterest", "YouTube", "Threads"];

  const processSteps = [
    { step: "01", title: "Discover", desc: "We audit your current social presence, analyze competitors, and research your target audience's behavior." },
    { step: "02", title: "Strategize", desc: "We build a custom strategy with defined goals, content pillars, platform priorities, and KPIs." },
    { step: "03", title: "Create", desc: "We develop scroll-stopping content—graphics, copy, and video—aligned to your brand voice." },
    { step: "04", title: "Engage", desc: "We manage your community daily—responding, initiating conversations, and nurturing loyalty." },
    { step: "05", title: "Optimize", desc: "We analyze performance data weekly and monthly, refining strategy and scaling what works." }
  ];

  const metrics = [
    { title: "Consistent Posting", value: "3.5x", desc: "More engagement for brands posting 5+ times per week compared to those posting sporadically." },
    { title: "Response Time", value: "7x", desc: "Increase in conversion likelihood when responding to your community within 1 hour." },
    { title: "Video Content", value: "2.5x", desc: "More engagement from short-form video formats versus traditional static image posts." },
    { title: "Brand Authenticity", value: "86%", desc: "Of consumers say authenticity is a key factor in deciding which brands they support." }
  ];

  const faqs = [
    { q: "Which social media platforms should my brand be on?", a: "It depends on your audience, industry, and goals. We analyze where your customers spend their time and focus your resources on the platforms that will deliver the highest ROI—rather than spreading thin everywhere." },
    { q: "How often will you post on my accounts?", a: "Posting frequency is tailored to each platform and your strategy. Typically we recommend 3–7 posts per week per platform, plus daily story/reel content where applicable. Quality and consistency always beat volume." },
    { q: "Do you create the content or do we need to provide it?", a: "We handle everything—from content ideation and copywriting to graphic design and video concepts. We collaborate with you for approvals and brand input, but the heavy lifting is on us." },
    { q: "How do you measure success?", a: "We track engagement rate, reach, impressions, follower growth, click-through rate, conversions, and sentiment. Every metric ladders up to your business goals. You receive detailed monthly reports with insights and recommendations." },
    { q: "Can you handle negative comments or a social media crisis?", a: "Absolutely. Our community management includes sentiment monitoring and a crisis response protocol. We address issues swiftly and professionally, protecting your brand reputation 24/7." },
    { q: "What does pricing look like?", a: "We offer flexible packages—monthly retainers scaled to your platform count and content needs, as well as project-based engagements. Every partnership starts with a social audit so we can scope what you actually need." }
  ];

  return (
    <div className="outpost-page min-h-screen text-black font-body selection:bg-black/10">
      <style>{outpostStyles}</style>
      <OutpostNavbar />

      {/* SECTION 1: Hero */}
      <section className="relative min-h-screen w-full flex flex-col items-center overflow-hidden pt-32 pb-24 px-8 md:px-16 lg:px-20">
        <FadingVideo 
          src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260418_094631_d30ab262-45ee-4b7d-99f3-5d5848c8ef13.mp4"
          className="absolute inset-0 w-full h-full object-cover z-0"
          maxOpacity={0.15}
        />

        <div className="relative z-10 w-full max-w-7xl mx-auto flex flex-col lg:flex-row gap-16 lg:gap-24 my-auto">
          
          {/* Left Text Block */}
          <div className="w-full lg:w-1/2 flex flex-col justify-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="text-sm font-body text-black/60 mb-6 uppercase tracking-widest"
            >
              // Expertise 05
            </motion.div>
            
            <BlurText 
              text="Social Media" 
              justify="flex-start"
              className="text-6xl md:text-7xl lg:text-[5.5rem] font-heading italic text-black leading-[0.85] tracking-[-3px] mb-8 text-left"
            />
            
            <motion.p 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 0.8 }}
              className="text-lg text-black/80 font-body font-light leading-relaxed max-w-xl mb-10"
            >
              Cultivate a loyal community and grow your brand's digital influence. We manage your presence around the clock with bespoke content strategies, proactive community management, and performance-driven engagement designed to spark conversation, strengthen relationships, and drive measurable results.
            </motion.p>

            <div className="flex flex-col gap-4 mb-10">
              <h4 className="font-heading italic text-2xl text-black mb-4">Core Focus</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8">
                {items.map((item, i) => (
                  <motion.div 
                    key={item}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + (i * 0.1) }}
                    className="flex items-center gap-3"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-black/40 shrink-0"></span>
                    <span className="text-sm font-medium uppercase tracking-wider text-black/80">{item}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.8 }}
            >
              <Link to="/contact" className="bg-black text-white rounded-full px-7 py-3.5 font-body font-medium flex items-center gap-2 hover:bg-black/80 transition-colors w-fit shadow-lg shadow-black/10">
                Start Your Social Growth Plan
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 ml-1"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </Link>
            </motion.div>
          </div>

          {/* Right Media Grid - Preserved 2x2 Masonry layout */}
          <div className="w-full lg:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-6 relative items-start lg:mt-8">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-white/20 blur-3xl rounded-full -z-10"></div>
            
            {/* Column 1 - Staggered Down */}
            <div className="flex flex-col gap-6 sm:mt-12">
              <motion.div 
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8 }}
                className="relative w-full aspect-[4/5] bg-black/5 rounded-[1.25rem] overflow-hidden liquid-glass flex flex-col items-center justify-center border border-black/5 shadow-sm"
              >
                <video src={videos[0]} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
              </motion.div>
              
              <motion.div 
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.8 }}
                className="relative w-full aspect-[4/5] bg-black/5 rounded-[1.25rem] overflow-hidden liquid-glass flex flex-col items-center justify-center border border-black/5 shadow-sm"
              >
                <video src={videos[2]} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
              </motion.div>
            </div>

            {/* Column 2 */}
            <div className="flex flex-col gap-6">
              <motion.div 
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }}
                className="relative w-full aspect-[4/5] bg-black/5 rounded-[1.25rem] overflow-hidden liquid-glass flex flex-col items-center justify-center border border-black/5 shadow-sm"
              >
                <video src={videos[1]} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
              </motion.div>
              
              <motion.div 
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
                className="relative w-full aspect-[4/5] bg-black/5 rounded-[1.25rem] overflow-hidden liquid-glass flex flex-col items-center justify-center border border-black/5 shadow-sm"
              >
                <video src={videos[3]} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
              </motion.div>
            </div>
            
          </div>
        </div>
      </section>

      {/* SECTION 2: Problem Statement */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20 bg-black/[0.02] border-y border-black/[0.05]">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="mb-16 max-w-3xl"
          >
            <h2 className="font-heading italic text-4xl md:text-5xl text-black mb-6">Why Most Brands Fail on Social Media</h2>
            <p className="text-black/70 font-body text-lg leading-relaxed">
              Algorithms reward consistency, community, and craft. We deliver all three. The average person scrolls through 300 feet of content per day. Without a strategy, your brand is invisible noise.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {problemStats.map((stat, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="liquid-glass rounded-2xl p-8 flex flex-col border border-black/5 hover:border-black/10 transition-colors shadow-sm"
              >
                <span className="font-heading italic text-5xl text-black mb-4">{stat.stat}</span>
                <p className="text-black/70 font-body text-sm leading-relaxed">{stat.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3: Capabilities */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20">
        <div className="max-w-7xl mx-auto">
           <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 text-center">
            <h2 className="font-heading italic text-4xl md:text-5xl text-black mb-6">Core Capabilities</h2>
            <p className="text-black/70 font-body text-lg max-w-2xl mx-auto">Comprehensive social management designed to build trust, spark conversations, and drive action.</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-center">
            {capabilities.map((cap, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="liquid-glass rounded-2xl p-8 flex flex-col shadow-sm border border-black/5 hover:border-black/10 transition-colors"
              >
                <h4 className="font-heading italic text-2xl text-black mb-3">{cap.title}</h4>
                <p className="text-black/70 font-body text-sm leading-relaxed">{cap.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4: Benefits */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20 bg-black/[0.02] border-y border-black/[0.05]">
         <div className="max-w-7xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 max-w-2xl">
               <h2 className="font-heading italic text-4xl md:text-5xl text-black mb-6">What You Can Expect</h2>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((b, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-2xl p-8 flex flex-col shadow-sm border border-black/5"
                >
                  <div className="text-3xl mb-4">{b.icon}</div>
                  <h4 className="font-body font-semibold text-black text-lg mb-2">{b.title}</h4>
                  <p className="text-black/70 font-body text-sm leading-relaxed">{b.desc}</p>
                </motion.div>
              ))}
            </div>
         </div>
      </section>

      {/* SECTION 5: Platform Expertise */}
      <section className="relative w-full py-24 px-8 md:px-16 lg:px-20 border-b border-black/[0.05]">
        <div className="max-w-5xl mx-auto text-center">
          <motion.h4 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="font-heading italic text-3xl md:text-4xl text-black mb-10">
            Platforms We Master
          </motion.h4>
          <div className="flex flex-wrap justify-center gap-4">
            {platforms.map((p, i) => (
              <motion.span 
                key={p}
                initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="px-6 py-3 rounded-full border border-black/10 text-black/80 font-body tracking-wide text-sm bg-black/[0.03] hover:bg-black/10 transition-colors shadow-sm"
              >
                {p}
              </motion.span>
            ))}
          </div>
          <motion.p 
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.4 }}
            className="mt-10 text-black/50 font-body text-sm max-w-xl mx-auto leading-relaxed"
          >
            We go where your audience is—and we optimize for each platform's unique algorithm, culture, and content format.
          </motion.p>
        </div>
      </section>

      {/* SECTION 6: Process */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 text-center">
            <h2 className="font-heading italic text-4xl md:text-5xl text-black mb-6">Our Process</h2>
            <p className="text-black/70 font-body text-lg max-w-2xl mx-auto">A systematic approach to growing your audience and establishing industry authority.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {processSteps.map((s, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="liquid-glass rounded-2xl p-6 flex flex-col shadow-sm"
              >
                <div className="flex items-center justify-between mb-8">
                  <span className="text-sm font-body uppercase tracking-widest text-black/40">Step</span>
                  <span className="text-3xl font-heading italic text-black/30">{s.step}</span>
                </div>
                <h4 className="font-body font-semibold text-black text-lg mb-3">{s.title}</h4>
                <p className="text-black/70 font-body text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7: Metrics */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20 bg-black/[0.02] border-y border-black/[0.05]">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="w-full lg:w-1/3">
            <h2 className="font-heading italic text-4xl md:text-5xl text-black mb-6">The Numbers That Matter</h2>
            <p className="text-black/70 font-body text-lg leading-relaxed mb-6">
              We don't chase trends blindly—we leverage data to ensure every strategy decision drives real business outcomes.
            </p>
          </motion.div>
          
          <div className="w-full lg:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {metrics.map((metric, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-black/5 flex flex-col shadow-sm"
              >
                <h4 className="font-body text-sm uppercase tracking-widest text-black/50 mb-2">{metric.title}</h4>
                <span className="font-heading italic text-5xl text-black mb-4">{metric.value}</span>
                <p className="text-black/70 font-body text-sm leading-relaxed">{metric.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 8: FAQ */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 text-center">
            <h2 className="font-heading italic text-4xl md:text-5xl text-black">Common Questions</h2>
          </motion.div>

          <div className="flex flex-col">
            {faqs.map((faq, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                <FAQItem q={faq.q} a={faq.a} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 9: Final CTA */}
      <section className="relative w-full pb-32 px-8 md:px-16 lg:px-20 flex justify-center">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
          className="liquid-glass-strong rounded-[2.5rem] p-12 md:p-24 w-full max-w-6xl text-center flex flex-col items-center border border-black/10 shadow-2xl shadow-black/5 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-white/40 to-transparent pointer-events-none"></div>
          
          <h2 className="font-heading italic text-5xl md:text-7xl text-black mb-8 relative z-10">Ready to Build a Community That Converts?</h2>
          <p className="text-black/70 font-body max-w-2xl text-lg md:text-xl mb-12 relative z-10">
            Your audience is already scrolling. The only question is whether they're stopping for your brand—or your competitor's. Let's make sure it's yours. Book a social media audit today and get a custom roadmap for growth.
          </p>
          <Link to="/contact" className="bg-black text-white rounded-full px-8 py-4 font-body font-medium flex items-center gap-2 hover:bg-black/80 transition-colors shadow-xl shadow-black/20 relative z-10 text-lg">
            Get My Social Audit
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 ml-1"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
          </Link>
        </motion.div>
      </section>

    </div>
  );
}